// src/CompleteProfileModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { saveAvatar } from "./utils/avatarService";
import { notificationService } from "./utils/notificationService";
import {
  BIO_MAX,
  COOLDOWN_MS,
  USERNAME_MAX,
  getSuggestions,
  isUsernameAvailable,
  normalize,
  saveUsernameAndProfile,
  setUsernameFlag,
  validateUsername,
} from "./utils/usernameService";
import "./CompleteProfileModal.css";


/**
 * Props
 *  uid               current user id
 *  mandatory         true  -> no close button, can't be dismissed (first-time setup)
 *                    false -> edit mode with a close button
 *  initialUsername   existing username ("" for first-time setup)
 *  suggestion        pre-filled username for first-time setup
 *  initialBio        existing bio
 *  usernameChangedAt ms timestamp of the last change (for the 30-day lock)
 *  avatarUrl         current avatar URL, if any
 *  onClose / onSaved callbacks
 */
const CompleteProfileModal = ({
  uid,
  mandatory = true,
  initialUsername = "",
  suggestion = "",
  initialBio = "",
  usernameChangedAt = null,
  avatarUrl = null,
  onClose,
  onSaved,
}) => {
  const [username, setUsername] = useState(initialUsername || suggestion || "");
  const [bio, setBio] = useState(initialBio || "");
  const [status, setStatus] = useState("idle"); // idle | checking | available | taken | invalid | same | error
  const [message, setMessage] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileRef = useRef(null);

  const name = normalize(username);

  // 30-day lock applies only when changing an existing username.
  const daysLeft =
    initialUsername && usernameChangedAt
      ? Math.ceil((usernameChangedAt + COOLDOWN_MS - Date.now()) / 86400000)
      : 0;
  const locked = daysLeft > 0;

  // Lock page scroll while the popup is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Photo preview URL cleanup.
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  // Live availability check: waits 500ms after typing stops, 1 read per check.
  useEffect(() => {
    if (locked) return undefined;

    setSuggestions([]);
    setSaveError("");

    if (initialUsername && name === initialUsername) {
      setStatus("same");
      return undefined;
    }
    if (!name) {
      setStatus("idle");
      return undefined;
    }
    const invalid = validateUsername(name);
    if (invalid) {
      setStatus("invalid");
      setMessage(invalid);
      return undefined;
    }

    setStatus("checking");
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const free = await isUsernameAvailable(name, uid);
        if (cancelled) return;
        if (free) {
          setStatus("available");
        } else {
          setStatus("taken");
          const alternatives = await getSuggestions(name, uid);
          if (!cancelled) setSuggestions(alternatives);
        }
      } catch (e) {
        console.error("Username check failed", e);
        if (!cancelled) setStatus("error");
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [name, initialUsername, uid, locked]);

  const canSave =
    !saving && (locked || status === "available" || status === "same");

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      notificationService.showError("Please choose an image file.");
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const uploadPhoto = async () => {
      const { url } = await saveAvatar({ uid, source: photoFile });
      return url;
    };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError("");
    try {
      const finalName = locked ? initialUsername : name;
      const cleanBio = bio.trim();
      await saveUsernameAndProfile({ uid, username: finalName, bio: cleanBio });

      let profileUrl = null;
      if (photoFile) {
        try {
          profileUrl = await uploadPhoto();
        } catch (e) {
          console.error("Photo upload failed", e);
          notificationService.showError("Username saved, but the photo upload failed.");
        }
      }

      setUsernameFlag(uid);
      window.dispatchEvent(
        new CustomEvent("profile-updated", {
          detail: {
            username: finalName,
            bio: cleanBio,
            usernameChangedAt:
              initialUsername && finalName !== initialUsername ? Date.now() : null,
            profileUrl,
          },
        })
      );
      notificationService.showSuccess(
        mandatory ? "Profile created!" : "Profile updated!"
      );
      if (onSaved) onSaved();
    } catch (e) {
      console.error("Save failed", e);
      if (e.code === "taken" || e.code === "permission-denied") {
        setStatus("taken");
        setSaveError("That username was just taken. Try another one.");
        getSuggestions(name, uid).then(setSuggestions).catch(() => {});
      } else if (e.code === "cooldown" || e.code === "invalid") {
        setSaveError(e.message);
      } else {
        setSaveError("Couldn't save. Check your connection and try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOverlayClick = () => {
    if (!mandatory && !saving && onClose) onClose();
  };

  const avatarSrc = photoPreview || avatarUrl;
  const avatarLetter = (name || "?").charAt(0).toUpperCase();

  const renderStatus = () => {
    if (locked) return null;
    switch (status) {
      case "checking":
        return <p className="cpm-status cpm-checking">Checking…</p>;
      case "available":
        return <p className="cpm-status cpm-ok">@{name} is available</p>;
      case "taken":
        return <p className="cpm-status cpm-bad">@{name} is taken</p>;
      case "invalid":
        return <p className="cpm-status cpm-bad">{message}</p>;
      case "error":
        return <p className="cpm-status cpm-bad">Couldn't check. Check your connection.</p>;
      default:
        return (
          <p className="cpm-status cpm-hint">
            3 to 20 characters: letters, numbers, _ and .
          </p>
        );
    }
  };

  return (
    <div className="cpm-overlay" onClick={handleOverlayClick}>
      <div
        className="cpm-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cpm-title"
        onClick={(e) => e.stopPropagation()}
      >
        {!mandatory && (
          <button
            type="button"
            className="cpm-close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        )}

        <h2 id="cpm-title" className="cpm-title">
          {mandatory ? "Complete your profile" : "Edit profile"}
        </h2>
        {mandatory && (
          <p className="cpm-sub">
            Choose a unique username. It's how people find you.
          </p>
        )}

        {/* Photo (optional) */}
        <div className="cpm-photo-row">
          <button
            type="button"
            className="cpm-avatar"
            onClick={() => fileRef.current && fileRef.current.click()}
            aria-label="Choose profile photo"
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt="" />
            ) : (
              <span>{avatarLetter}</span>
            )}
          </button>
          <div className="cpm-photo-text">
            <button
              type="button"
              className="cpm-link-btn"
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              {avatarSrc ? "Change photo" : "Add photo"}
            </button>
            <span className="cpm-optional">Optional</span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoPick}
            hidden
          />
        </div>

        {/* Username */}
        <label className="cpm-label" htmlFor="cpm-username">
          Username
        </label>
        <div className={`cpm-input-wrap ${status === "available" ? "is-ok" : ""} ${status === "taken" || status === "invalid" ? "is-bad" : ""}`}>
          <span className="cpm-at">@</span>
          <input
            id="cpm-username"
            className="cpm-input"
            type="text"
            value={username}
            onChange={(e) =>
              setUsername(e.target.value.toLowerCase().replace(/\s/g, ""))
            }
            maxLength={USERNAME_MAX}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            disabled={locked || saving}
            autoFocus={!locked}
          />
        </div>

        {locked ? (
          <p className="cpm-status cpm-hint">
            You can change your username again in {daysLeft} day{daysLeft === 1 ? "" : "s"}.
          </p>
        ) : (
          renderStatus()
        )}

        {suggestions.length > 0 && (
          <div className="cpm-suggestions">
            <span className="cpm-suggest-label">Try:</span>
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="cpm-chip"
                onClick={() => setUsername(s)}
              >
                @{s}
              </button>
            ))}
          </div>
        )}

        {/* Bio */}
        <label className="cpm-label" htmlFor="cpm-bio">
          My story
        </label>
        <textarea
          id="cpm-bio"
          className="cpm-textarea"
          value={bio}
          onChange={(e) => {
            if (e.target.value.length <= BIO_MAX) setBio(e.target.value);
          }}
          placeholder="Tell your story..."
          rows={4}
          disabled={saving}
        />
        <div className="cpm-counter">
          {bio.length}/{BIO_MAX}
        </div>

        {saveError && <p className="cpm-status cpm-bad cpm-save-error">{saveError}</p>}

        <button
          type="button"
          className="cpm-save"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? "Saving…" : mandatory ? "Continue" : "Save changes"}
        </button>
      </div>
    </div>
  );
};

export default CompleteProfileModal;